import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { brl, data } from "@/lib/format";
import { paraCentavos, paraCampo } from "@/lib/dinheiro";
import {
  PageHeader,
  Panel,
  Th,
  Td,
  EmptyState,
  StatusBadge,
  Campo,
  Escolha,
  Quorum,
  Erro,
} from "@/components/parker";
import {
  useEmpresas,
  useCategorias,
  useCentros,
  useContrapartes,
  useContratos,
  useAprovacoes,
  useIndice,
} from "@/lib/dados";
import { criarContrato, atualizarContrato, gerarParcelas, registrarAprovacao } from "@/lib/fns";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PERIODICIDADES } from "@/lib/tipos";
import { Historico } from "@/components/historico";
import { History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/contratos")({ component: Contratos });

const hoje = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const FORM_INICIAL = {
  descricao: "",
  contraparteId: "",
  empresaPagadoraId: "",
  empresaBeneficiadaId: "",
  categoriaId: "",
  centroCustoId: "",
  valor: "",
  periodicidade: "mensal",
  inicio: hoje(),
  fim: "",
  avisoPrevioDias: "30",
  reajusteIndice: "IPCA",
};

function Contratos() {
  const { podeEscrever, podeAprovar, usuario } = useSessao();
  const empresas = useEmpresas();
  const categorias = useCategorias();
  const centros = useCentros();
  const contrapartes = useContrapartes();
  const contratos = useContratos();
  const aprovacoes = useAprovacoes();

  const nomeEmpresa = useIndice(empresas.dados);
  const nomeContraparte = useIndice(contrapartes.dados);

  const [aberto, setAberto] = useState(false);
  const [f, setF] = useState(FORM_INICIAL);
  const [ocupado, setOcupado] = useState<string | null>(null);
  // Qual contrato está com o histórico expandido.
  const [historico, setHistorico] = useState<string | null>(null);
  // Id do contrato em edição; null quando o formulário é de criação.
  const [editando, setEditando] = useState<string | null>(null);

  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  if (contratos.erro) return <Erro mensagem={contratos.erro} />;

  const validas = aprovacoes.dados.filter((a) => !a.invalidada);
  const assinaturas = (contratoId: string) =>
    new Set(
      validas
        .filter((a) => a.contratoId === contratoId && a.decisao === "aprovado")
        .map((a) => a.userId),
    ).size;
  const jaAssinei = (contratoId: string) =>
    validas.some((a) => a.contratoId === contratoId && a.userId === usuario?.uid);

  /** Contrato com fim próximo precisa de decisão antes do aviso prévio vencer. */
  const emAviso = (fim: string | null, dias: number) => {
    if (!fim) return false;
    const restam = (new Date(`${fim}T00:00:00`).getTime() - Date.now()) / 864e5;
    return restam > 0 && restam <= dias;
  };

  function editar(c: (typeof contratos.dados)[number]) {
    setF({
      descricao: c.descricao,
      contraparteId: c.contraparteId ?? "",
      empresaPagadoraId: c.empresaPagadoraId,
      empresaBeneficiadaId: c.empresaBeneficiadaId,
      categoriaId: c.categoriaId,
      centroCustoId: c.centroCustoId,
      valor: paraCampo(c.valorCentavos),
      periodicidade: c.periodicidade,
      inicio: c.inicio,
      fim: c.fim ?? "",
      avisoPrevioDias: String(c.avisoPrevioDias ?? 30),
      reajusteIndice: c.reajusteIndice ?? "",
    });
    setEditando(c.id);
    setAberto(true);
  }

  async function salvar() {
    setOcupado("novo");
    try {
      const dados = {
        descricao: f.descricao,
        contraparteId: f.contraparteId || null,
        empresaPagadoraId: f.empresaPagadoraId || null,
        empresaBeneficiadaId: f.empresaBeneficiadaId,
        categoriaId: f.categoriaId,
        centroCustoId: f.centroCustoId,
        valorCentavos: paraCentavos(f.valor),
        periodicidade: f.periodicidade,
        inicio: f.inicio,
        fim: f.fim || null,
        avisoPrevioDias: Number(f.avisoPrevioDias) || 30,
        reajusteIndice: f.reajusteIndice || null,
      };

      if (editando) {
        const r = await atualizarContrato({ ...dados, id: editando });
        toast.success(
          r.aprovacoesInvalidadas
            ? `Alteração material (${r.mudancas.join(", ")}) — o contrato volta para aprovação com ${r.aprovacoesNecessarias} assinatura(s).`
            : "Contrato atualizado.",
        );
      } else {
        await criarContrato(dados);
        toast.success("Contrato criado e enviado para aprovação.");
      }

      setF(FORM_INICIAL);
      setAberto(false);
      setEditando(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function aprovar(id: string) {
    setOcupado(id);
    try {
      const r = await registrarAprovacao({ contratoId: id, decisao: "aprovado" });
      toast.success(
        r.status === "aprovado"
          ? "Contrato aprovado."
          : "Aprovação registrada. Faltam assinaturas.",
      );
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  async function gerar(id: string) {
    setOcupado(id);
    try {
      const r = await gerarParcelas({ id });
      toast.success(
        r.geradas > 0
          ? `${r.geradas} parcelas geradas, já aprovadas pelo contrato.`
          : (r.mensagem ?? "Nenhuma parcela nova."),
      );
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  const despesas = categorias.dados.filter((c) => c.tipo === "despesa");

  return (
    <>
      <PageHeader
        title="Contratos"
        subtitle="Contrato aprovado gera parcelas automaticamente; cada parcela é conciliada sem reaprovar o contrato."
        actions={
          podeEscrever ? (
            <Button
              onClick={() => {
                setAberto((v) => !v);
                setEditando(null);
                setF(FORM_INICIAL);
              }}
            >
              {aberto ? "Fechar" : "Novo contrato"}
            </Button>
          ) : undefined
        }
      />

      {aberto ? (
        <Panel className="mb-4 grid gap-4 p-5 sm:grid-cols-3">
          <Campo label="Descrição">
            <Input value={f.descricao} onChange={(e) => set("descricao", e.target.value)} />
          </Campo>
          <Campo label="Fornecedor">
            <Escolha
              value={f.contraparteId}
              onChange={(v) => set("contraparteId", v)}
              permitirVazio
              placeholder="Sem fornecedor"
              options={contrapartes.dados.map((c) => ({ id: c.id, label: c.nome }))}
            />
          </Campo>
          <Campo label="Valor da parcela (R$)">
            <Input
              className="num"
              inputMode="decimal"
              placeholder="0,00"
              value={f.valor}
              onChange={(e) => set("valor", e.target.value)}
            />
          </Campo>
          <Campo label="Empresa que paga">
            <Escolha
              value={f.empresaPagadoraId}
              onChange={(v) => set("empresaPagadoraId", v)}
              options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
            />
          </Campo>
          <Campo label="Empresa beneficiada">
            <Escolha
              value={f.empresaBeneficiadaId}
              onChange={(v) => set("empresaBeneficiadaId", v)}
              options={empresas.dados.map((e) => ({ id: e.id, label: e.nome }))}
            />
          </Campo>
          <Campo label="Categoria">
            <Escolha
              value={f.categoriaId}
              onChange={(v) => set("categoriaId", v)}
              options={despesas.map((c) => ({ id: c.id, label: `${c.codigo} · ${c.nome}` }))}
            />
          </Campo>
          <Campo label="Centro de custo">
            <Escolha
              value={f.centroCustoId}
              onChange={(v) => set("centroCustoId", v)}
              options={centros.dados.map((c) => ({ id: c.id, label: `${c.codigo} · ${c.nome}` }))}
            />
          </Campo>
          <Campo label="Periodicidade">
            <Escolha
              value={f.periodicidade}
              onChange={(v) => set("periodicidade", v)}
              options={PERIODICIDADES.map((p) => ({ id: p, label: p }))}
            />
          </Campo>
          <Campo label="Reajuste">
            <Input
              value={f.reajusteIndice}
              onChange={(e) => set("reajusteIndice", e.target.value)}
            />
          </Campo>
          <Campo label="Início">
            <Input type="date" value={f.inicio} onChange={(e) => set("inicio", e.target.value)} />
          </Campo>
          <Campo label="Fim da vigência" hint="Em branco: indeterminada.">
            <Input type="date" value={f.fim} onChange={(e) => set("fim", e.target.value)} />
          </Campo>
          <Campo label="Aviso prévio (dias)">
            <Input
              className="num"
              value={f.avisoPrevioDias}
              onChange={(e) => set("avisoPrevioDias", e.target.value)}
            />
          </Campo>
          <div className="sm:col-span-3 flex items-center gap-3">
            <Button onClick={() => void salvar()} disabled={ocupado === "novo"}>
              {editando ? "Salvar alteração" : "Criar contrato"}
            </Button>
            {editando ? (
              <span className="text-xs text-muted-foreground">
                Mudar objeto, fornecedor, valor, periodicidade, beneficiada, categoria, centro de
                custo ou vigência derruba as aprovações e devolve o contrato para a fila.
              </span>
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-sm">
          <thead>
            <tr>
              <Th>Contrato</Th>
              <Th>Fornecedor</Th>
              <Th>Beneficiada</Th>
              <Th>Periodicidade</Th>
              <Th>Vigência</Th>
              <Th className="text-right">Parcela</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {contratos.dados.map((c) => {
              const feitas = assinaturas(c.id);
              return (
                <Fragment key={c.id}>
                  <tr>
                    <Td>
                      <div className="max-w-[240px] truncate">{c.descricao}</div>
                      {emAviso(c.fim, c.avisoPrevioDias) ? (
                        <div className="label-caps text-warning">renovação próxima</div>
                      ) : null}
                      {c.parcelasGeradasAte ? (
                        <div className="label-caps">parcelas até {data(c.parcelasGeradasAte)}</div>
                      ) : null}
                    </Td>
                    <Td className="text-muted-foreground">
                      {c.contraparteId ? (nomeContraparte.get(c.contraparteId) ?? "—") : "—"}
                    </Td>
                    <Td>{nomeEmpresa.get(c.empresaBeneficiadaId) ?? "—"}</Td>
                    <Td className="text-muted-foreground">{c.periodicidade}</Td>
                    <Td className="num whitespace-nowrap">
                      {data(c.inicio)} → {c.fim ? data(c.fim) : "indeterminada"}
                    </Td>
                    <Td className="num text-right">{brl(c.valorCentavos)}</Td>
                    <Td>
                      <StatusBadge status={c.status} />
                      {c.status !== "aprovado" ? (
                        <div className="mt-1.5 w-24">
                          <Quorum feitas={feitas} necessarias={c.aprovacoesNecessarias} />
                          <div className="label-caps mt-1">
                            {feitas}/{c.aprovacoesNecessarias}
                          </div>
                        </div>
                      ) : null}
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Histórico deste contrato"
                          onClick={() => setHistorico((h) => (h === c.id ? null : c.id))}
                        >
                          <History className="size-3.5" />
                        </Button>
                        {podeAprovar && c.status !== "aprovado" && c.status !== "rejeitado" ? (
                          jaAssinei(c.id) ? (
                            <span className="text-xs text-muted-foreground">você já assinou</span>
                          ) : (
                            <Button
                              size="sm"
                              disabled={ocupado === c.id}
                              onClick={() => void aprovar(c.id)}
                            >
                              Aprovar
                            </Button>
                          )
                        ) : null}
                        {podeEscrever && c.status !== "rejeitado" ? (
                          <Button size="sm" variant="ghost" onClick={() => editar(c)}>
                            Editar
                          </Button>
                        ) : null}
                        {podeEscrever && c.status === "aprovado" ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={ocupado === c.id}
                            onClick={() => void gerar(c.id)}
                          >
                            Gerar parcelas
                          </Button>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                  {historico === c.id ? (
                    <tr>
                      <td colSpan={8} className="border-b border-hairline bg-surface-2/40 p-0">
                        <div className="px-2 py-1">
                          <div className="label-caps px-3 pt-2">Histórico do contrato</div>
                          <Historico entidade="contratos" entidadeId={c.id} />
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {contratos.dados.length === 0 ? (
          <EmptyState
            title="Nenhum contrato cadastrado"
            hint="Contratos recorrentes evitam reaprovar a mesma despesa todo mês."
          />
        ) : null}
      </Panel>
    </>
  );
}
